import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Checkbox } from './ui/checkbox';
import { Search, X, BarChart3, Users, UserPlus, Building2, Download } from 'lucide-react';
import RadarChart from './RadarChart';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useLanguage } from '../contexts/LanguageContext';
import logger from '../utils/logger';

// Separate component for Ofertas chart to avoid re-render issues
const OfertasChart = ({ comparisonResult, colors, referenceColor, t }) => {
  const { language } = useLanguage();
  const chartData = useMemo(() => {
    if (!comparisonResult || !comparisonResult.labels || !comparisonResult.comparison_data) {
      return null;
    }


    const datasets = [
      ...(comparisonResult.comparison_data || []).map((item, idx) => ({
        label: item.name,
        data: item.ofertas || [],
        backgroundColor: colors[idx]?.bg || 'rgba(59, 130, 246, 0.15)',
        borderColor: colors[idx]?.border || 'rgba(59, 130, 246, 1)',
        borderWidth: 3,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: colors[idx]?.border || 'rgba(59, 130, 246, 1)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2
      })),
      ...(comparisonResult.reference_data ? [{
        label: comparisonResult.reference_data.name,
        data: comparisonResult.reference_data.ofertas || [],
        backgroundColor: referenceColor.bg,
        borderColor: referenceColor.border,
        borderWidth: 5,
        borderDash: [8, 4],
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBackgroundColor: referenceColor.border,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointStyle: 'rectRot'
      }] : [])
    ];


    if (datasets.length === 0) {
      return null;
    }

    return {
      labels: comparisonResult.labels || [],
      datasets: datasets
    };
  }, [comparisonResult, colors, referenceColor]);

  if (!chartData) {
    return (
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="w-3 h-3 rounded-full bg-blue-500 mr-2"></span>
          {t('charts.ofertas', 'Ofertas')}
        </h3>
        <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
          <p className="text-gray-500">{t('charts.no_data_available', 'No data available')}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4 flex items-center">
        <span className="w-3 h-3 rounded-full bg-blue-500 mr-2"></span>
        {t('charts.ofertas', 'Ofertas')}
      </h3>
      <RadarChart 
        key={`ofertas-advanced-${language}`}
        data={chartData}
        type="comparison"
        width={500}
        height={400}
        className="w-full"
      />
    </div>
  );
};

// Separate component for Requerimientos chart
const RequerimientosChart = ({ comparisonResult, colors, referenceColor, t }) => {
  const { language } = useLanguage();
  const chartData = useMemo(() => {
    if (!comparisonResult || !comparisonResult.labels || !comparisonResult.comparison_data) {
      return null;
    }


    const datasets = [
      ...(comparisonResult.comparison_data || []).map((item, idx) => ({
        label: item.name,
        data: item.requerimientos || [],
        backgroundColor: colors[idx]?.bg || 'rgba(239, 68, 68, 0.15)',
        borderColor: colors[idx]?.border || 'rgba(239, 68, 68, 1)',
        borderWidth: 3,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: colors[idx]?.border || 'rgba(239, 68, 68, 1)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2
      })),
      ...(comparisonResult.reference_data ? [{
        label: comparisonResult.reference_data.name,
        data: comparisonResult.reference_data.requerimientos || [],
        backgroundColor: referenceColor.bg,
        borderColor: referenceColor.border,
        borderWidth: 5,
        borderDash: [8, 4],
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBackgroundColor: referenceColor.border,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointStyle: 'rectRot'
      }] : [])
    ];


    if (datasets.length === 0) {
      return null;
    }

    return {
      labels: comparisonResult.labels || [],
      datasets: datasets
    };
  }, [comparisonResult, colors, referenceColor]);

  if (!chartData) {
    return (
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <span className="w-3 h-3 rounded-full bg-red-500 mr-2"></span>
          {t('charts.requerimientos', 'Requerimientos')}
        </h3>
        <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
          <p className="text-gray-500">{t('charts.no_data_available', 'No data available')}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4 flex items-center">
        <span className="w-3 h-3 rounded-full bg-red-500 mr-2"></span>
        {t('charts.requerimientos', 'Requerimientos')}
      </h3>
      <RadarChart 
        key={`requerimientos-advanced-${language}`}
        data={chartData}
        type="comparison"
        width={500}
        height={400}
        className="w-full"
      />
    </div>
  );
};

const AdvancedComparisonSection = ({ t }) => {
  const { language } = useLanguage();
  const [employees, setEmployees] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [departments, setDepartments] = useState([]);
  
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  
  const [compareAgainst, setCompareAgainst] = useState('employee_average');
  const [comparisonResult, setComparisonResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [candidateSearch, setCandidateSearch] = useState('');
  const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);
  const [candidateDropdownOpen, setCandidateDropdownOpen] = useState(false);
  
  const comparisonResultsRef = useRef(null);

  // Load data
  useEffect(() => {
    loadEmployees();
    loadCandidates();
    loadDepartments();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setEmployeeDropdownOpen(false);
        setCandidateDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadEmployees = async () => {
    try {
      const response = await axios.get('/companies/employees-and-candidates');
      if (response.data.success) {
        // Filter only employees with completed assessments and sort alphabetically
        const allUsers = response.data.data || [];
        const empWithAssessments = allUsers
          .filter(user => user.rol === 'employee' && user.completed_assessments > 0)
          .sort((a, b) => a.nombre.localeCompare(b.nombre));
        setEmployees(empWithAssessments);
      }
    } catch (error) {
      logger.error('Error loading employees:', error);
    }
  };

  const loadCandidates = async () => {
    try {
      const response = await axios.get('/companies/employees-and-candidates');
      if (response.data.success) {
        const allUsers = response.data.data || [];
        const candWithAssessments = allUsers
          .filter(user => user.rol === 'candidate' && user.completed_assessments > 0)
          .sort((a, b) => a.nombre.localeCompare(b.nombre));
        setCandidates(candWithAssessments);
      }
    } catch (error) {
      logger.error('Error loading candidates:', error);
    }
  };

  const loadDepartments = async () => {
    try {
      const response = await axios.get('/companies/departments');
      if (response.data.success) {
        setDepartments(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading departments:', error);
    }
  };

  // Selection handlers
  const toggleEmployee = (empId) => {
    const total = selectedEmployees.length + selectedCandidates.length + selectedDepartments.length;
    if (selectedEmployees.includes(empId)) {
      setSelectedEmployees(selectedEmployees.filter(id => id !== empId));
    } else if (total < 6) {
      setSelectedEmployees([...selectedEmployees, empId]);
    } else {
      alert(t('reports.advanced.max_selection', 'Maximum 6 entities can be selected'));
    }
  };

  const toggleCandidate = (candId) => {
    const total = selectedEmployees.length + selectedCandidates.length + selectedDepartments.length;
    if (selectedCandidates.includes(candId)) {
      setSelectedCandidates(selectedCandidates.filter(id => id !== candId));
    } else if (total < 6) {
      setSelectedCandidates([...selectedCandidates, candId]);
    } else {
      alert(t('reports.advanced.max_selection', 'Maximum 6 entities can be selected'));
    }
  };

  const toggleDepartment = (deptName) => {
    const total = selectedEmployees.length + selectedCandidates.length + selectedDepartments.length;
    if (selectedDepartments.includes(deptName)) {
      setSelectedDepartments(selectedDepartments.filter(name => name !== deptName));
    } else if (total < 6) {
      setSelectedDepartments([...selectedDepartments, deptName]);
    } else {
      alert(t('reports.advanced.max_selection', 'Maximum 6 entities can be selected'));
    }
  };

  const clearSelection = () => {
    setSelectedEmployees([]);
    setSelectedCandidates([]);
    setSelectedDepartments([]);
  };

  const generateComparison = async () => {
    const total = selectedEmployees.length + selectedCandidates.length + selectedDepartments.length;
    if (total === 0) {
      alert(t('reports.advanced.select_at_least_one', 'Please select at least one entity'));
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `/reports/advanced-comparison?language=${language}`,
        {
          employee_ids: selectedEmployees,
          candidate_ids: selectedCandidates,
          departments: selectedDepartments,
          compare_against: compareAgainst
        }
      );


      if (response.data.success) {
        setComparisonResult(response.data.data);
      }
    } catch (error) {
      logger.error('Error generating comparison:', error);
      logger.error('Error details:', error.response?.data);
      alert(t('reports.advanced.error', 'Error generating comparison') + ': ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = async () => {
    if (!comparisonResultsRef.current) return;
    
    setExportingPDF(true);
    try {
      const pdf = new jsPDF('portrait', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const headerHeight = 50;
      const footerHeight = 15;
      const contentAreaHeight = pageHeight - headerHeight - footerHeight;
      
      // Load logo
      const loadLogoForPDF = () => {
        return new Promise((resolve) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/png'));
          };
          img.onerror = () => resolve(null);
          img.src = '/images/mevia-logo-horizontal.png';
        });
      };
      
      const logoData = await loadLogoForPDF();
      
      // Add logo
      if (logoData) {
        pdf.addImage(logoData, 'PNG', margin, 8, 35, 12);
      }
      
      // Add teal accent line
      pdf.setDrawColor(26, 135, 141);
      pdf.setLineWidth(0.5);
      pdf.line(margin, 22, pageWidth - margin, 22);
      
      // Add title
      pdf.setFontSize(14);
      pdf.setTextColor(11, 106, 125);
      pdf.text(t('reports.advanced.title', 'Advanced Comparison'), margin + 40, 16);
      pdf.setTextColor(0, 0, 0);
      
      // Add comparison type info
      pdf.setFontSize(10);
      const comparisonType = compareAgainst === 'employee_average' 
        ? t('reports.advanced.employee_average', 'Employee Average')
        : t('reports.advanced.company_profile', 'Company Profile');
      pdf.text(`${t('reports.advanced.compare_against', 'Compare Against')}: ${comparisonType}`, margin, 30);
      
      // Add selected entities info
      pdf.setFontSize(9);
      if (comparisonResult && comparisonResult.comparison_data) {
        const entities = comparisonResult.comparison_data.map(item => item.full_name || item.name).join(', ');
        const maxWidth = pageWidth - (2 * margin);
        const splitEntities = pdf.splitTextToSize(`${t('reports.advanced.selected', 'Selected')}: ${entities}`, maxWidth);
        pdf.text(splitEntities, margin, 37);
      }
      
      // Capture the comparison results section as image
      const canvas = await html2canvas(comparisonResultsRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: comparisonResultsRef.current.scrollWidth,
        windowHeight: comparisonResultsRef.current.scrollHeight
      });
      
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = pageWidth - (2 * margin);
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Calculate how many pages we need
      const totalPages = Math.ceil(imgHeight / contentAreaHeight);
      
      // For each page
      for (let page = 0; page < totalPages; page++) {
        if (page > 0) {
          pdf.addPage();
        }
        
        // Add header on each page
        if (page === 0) {
          // First page already has the header from above
        } else {
          pdf.setFontSize(16);
          pdf.text(t('reports.advanced.title', 'Advanced Comparison'), margin, 15);
          pdf.setFontSize(10);
          pdf.text(`${t('reports.continued', 'Continued')}...`, margin, 23);
        }
        
        // Calculate the portion of the canvas to show on this page
        const srcY = (page * contentAreaHeight * canvas.width) / imgWidth;
        const srcHeight = (contentAreaHeight * canvas.width) / imgWidth;
        
        // Create a temporary canvas for this page's content
        const pageCanvas = document.createElement('canvas');
        const pageContext = pageCanvas.getContext('2d');
        pageCanvas.width = canvas.width;
        pageCanvas.height = Math.min(srcHeight, canvas.height - srcY);
        
        // Draw the portion of the original canvas
        pageContext.drawImage(
          canvas,
          0, srcY,
          canvas.width, pageCanvas.height,
          0, 0,
          canvas.width, pageCanvas.height
        );
        
        const pageImgData = pageCanvas.toDataURL('image/png');
        const pageImgHeight = (pageCanvas.height * imgWidth) / pageCanvas.width;
        
        // Add this page's image
        pdf.addImage(pageImgData, 'PNG', margin, headerHeight, imgWidth, pageImgHeight);
        
        // Add footer with MEVIA branding
        pdf.setDrawColor(26, 135, 141);
        pdf.setLineWidth(0.3);
        pdf.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text(
          `MEVIA - Data With Soul | ${new Date().toLocaleDateString()} | ${language === 'es' ? 'Página' : 'Page'} ${page + 1} ${language === 'es' ? 'de' : 'of'} ${totalPages}`,
          margin,
          pageHeight - 10
        );
        pdf.setTextColor(0, 0, 0);
      }
      
      // Save the PDF
      const fileName = `advanced_comparison_${new Date().getTime()}.pdf`;
      pdf.save(fileName);
      
    } catch (error) {
      logger.error('Error exporting PDF:', error);
      alert(t('reports.advanced.export_error', 'Error exporting PDF') + ': ' + error.message);
    } finally {
      setExportingPDF(false);
    }
  };

  // Filter functions
  const filteredEmployees = employees.filter(emp =>
    emp.nombre.toLowerCase().includes(employeeSearch.toLowerCase())
  );

  const filteredCandidates = candidates.filter(cand =>
    cand.nombre.toLowerCase().includes(candidateSearch.toLowerCase())
  );

  const totalSelected = selectedEmployees.length + selectedCandidates.length + selectedDepartments.length;

  // Colors for chart lines - memoized for performance
  const colors = useMemo(() => [
    { bg: 'rgba(59, 130, 246, 0.2)', border: 'rgba(59, 130, 246, 1)' }, // Blue
    { bg: 'rgba(239, 68, 68, 0.2)', border: 'rgba(239, 68, 68, 1)' },   // Red
    { bg: 'rgba(34, 197, 94, 0.2)', border: 'rgba(34, 197, 94, 1)' },   // Green
    { bg: 'rgba(251, 191, 36, 0.2)', border: 'rgba(251, 191, 36, 1)' }, // Yellow
    { bg: 'rgba(168, 85, 247, 0.2)', border: 'rgba(168, 85, 247, 1)' }, // Purple
    { bg: 'rgba(236, 72, 153, 0.2)', border: 'rgba(236, 72, 153, 1)' }  // Pink
  ], []);

  const referenceColor = useMemo(() => {
    const color = { bg: 'rgba(255, 140, 0, 0.15)', border: 'rgba(255, 140, 0, 1)' };
    return color;
  }, []); // Dark orange for reference - highly visible

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <BarChart3 className="h-6 w-6 mr-2 text-purple-600" />
            {t('reports.advanced.title', 'Advanced Comparison')}
          </CardTitle>
          <CardDescription>
            {t('reports.advanced.description', 'Compare up to 6 employees, candidates, or departments against a reference baseline')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Employees Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <Users className="h-5 w-5 mr-2" />
                  {t('reports.advanced.employees', 'Employees')}
                  {selectedEmployees.length > 0 && (
                    <Badge variant="secondary" className="ml-2">{selectedEmployees.length}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Search input */}
                <div className="relative dropdown-container">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder={t('reports.advanced.search', 'Search...')}
                    value={employeeSearch}
                    onChange={(e) => setEmployeeSearch(e.target.value)}
                    onFocus={() => setEmployeeDropdownOpen(true)}
                    className="pl-9"
                  />
                  
                  {/* Dropdown with filtered results */}
                  {employeeDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {filteredEmployees.slice(0, 10).map(emp => (
                        <div 
                          key={emp.id} 
                          className="flex items-center space-x-2 p-3 hover:bg-gray-100 cursor-pointer border-b"
                          onClick={() => {
                            if (selectedEmployees.includes(emp.id)) {
                              setSelectedEmployees(selectedEmployees.filter(id => id !== emp.id));
                            } else if (totalSelected < 6) {
                              setSelectedEmployees([...selectedEmployees, emp.id]);
                              setEmployeeSearch('');
                              setEmployeeDropdownOpen(false);
                            } else {
                              alert(t('reports.advanced.max_selection', 'Maximum 6 entities can be selected'));
                            }
                          }}
                        >
                          <Checkbox
                            checked={selectedEmployees.includes(emp.id)}
                            readOnly
                          />
                          <div className="flex-1">
                            <div className="text-sm font-medium">{emp.nombre}</div>
                            {emp.departamento && (
                              <div className="text-xs text-gray-500">{emp.departamento}</div>
                            )}
                          </div>
                        </div>
                      ))}
                      {filteredEmployees.length === 0 && (
                        <div className="p-3 text-sm text-gray-500">
                          {t('reports.advanced.no_results', 'No results found')}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Selected employees chips */}
                {selectedEmployees.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedEmployees.map(empId => {
                      const emp = employees.find(e => e.id === empId);
                      return emp ? (
                        <Badge key={emp.id} variant="secondary" className="flex items-center gap-1">
                          {emp.nombre.split(' ')[0]}
                          <X 
                            className="h-3 w-3 cursor-pointer" 
                            onClick={() => toggleEmployee(emp.id)}
                          />
                        </Badge>
                      ) : null;
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Candidates Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <UserPlus className="h-5 w-5 mr-2" />
                  {t('reports.advanced.candidates', 'Candidates')}
                  {selectedCandidates.length > 0 && (
                    <Badge variant="secondary" className="ml-2">{selectedCandidates.length}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Search input */}
                <div className="relative dropdown-container">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder={t('reports.advanced.search', 'Search...')}
                    value={candidateSearch}
                    onChange={(e) => setCandidateSearch(e.target.value)}
                    onFocus={() => setCandidateDropdownOpen(true)}
                    className="pl-9"
                  />
                  
                  {/* Dropdown with filtered results */}
                  {candidateDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {filteredCandidates.slice(0, 10).map(cand => (
                        <div 
                          key={cand.id} 
                          className="flex items-center space-x-2 p-3 hover:bg-gray-100 cursor-pointer border-b"
                          onClick={() => {
                            if (selectedCandidates.includes(cand.id)) {
                              setSelectedCandidates(selectedCandidates.filter(id => id !== cand.id));
                            } else if (totalSelected < 6) {
                              setSelectedCandidates([...selectedCandidates, cand.id]);
                              setCandidateSearch('');
                              setCandidateDropdownOpen(false);
                            } else {
                              alert(t('reports.advanced.max_selection', 'Maximum 6 entities can be selected'));
                            }
                          }}
                        >
                          <Checkbox
                            checked={selectedCandidates.includes(cand.id)}
                            readOnly
                          />
                          <div className="flex-1">
                            <div className="text-sm font-medium">{cand.nombre}</div>
                          </div>
                        </div>
                      ))}
                      {filteredCandidates.length === 0 && (
                        <div className="p-3 text-sm text-gray-500">
                          {t('reports.advanced.no_results', 'No results found')}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Selected candidates chips */}
                {selectedCandidates.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedCandidates.map(candId => {
                      const cand = candidates.find(c => c.id === candId);
                      return cand ? (
                        <Badge key={cand.id} variant="secondary" className="flex items-center gap-1">
                          {cand.nombre.split(' ')[0]}
                          <X 
                            className="h-3 w-3 cursor-pointer" 
                            onClick={() => toggleCandidate(cand.id)}
                          />
                        </Badge>
                      ) : null;
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Departments Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <Building2 className="h-5 w-5 mr-2" />
                  {t('reports.advanced.departments', 'Departments')}
                  {selectedDepartments.length > 0 && (
                    <Badge variant="secondary" className="ml-2">{selectedDepartments.length}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {departments.map(dept => (
                    <div key={dept.name} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                      <Checkbox
                        checked={selectedDepartments.includes(dept.name)}
                        onCheckedChange={() => toggleDepartment(dept.name)}
                        disabled={!selectedDepartments.includes(dept.name) && totalSelected >= 6}
                      />
                      <label className="flex-1 text-sm cursor-pointer">
                        {dept.name}
                        <span className="text-xs text-gray-500 ml-2">({dept.count} emp.)</span>
                      </label>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Compare Against Section */}
          <Card className="mt-6">
            <CardContent className="pt-6">
              <div className="space-y-4">
                <Label className="text-base font-semibold">
                  {t('reports.advanced.compare_against', 'Compare Against')}
                </Label>
                <RadioGroup value={compareAgainst} onValueChange={setCompareAgainst}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="employee_average" id="emp_avg" />
                    <Label htmlFor="emp_avg" className="cursor-pointer">
                      {t('reports.advanced.employee_average', 'Employee Average')}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="company_profile" id="co_profile" />
                    <Label htmlFor="co_profile" className="cursor-pointer">
                      {t('reports.advanced.company_profile', 'Company Profile')}
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex items-center justify-between mt-6">
            <div className="text-sm text-gray-600">
              {t('reports.advanced.selected', 'Selected')}: <strong>{totalSelected}/6</strong>
            </div>
            <div className="flex space-x-3">
              <Button variant="outline" onClick={clearSelection} disabled={totalSelected === 0}>
                <X className="h-4 w-4 mr-2" />
                {t('reports.advanced.clear', 'Clear')}
              </Button>
              <Button onClick={generateComparison} disabled={loading || totalSelected === 0}>
                <BarChart3 className="h-4 w-4 mr-2" />
                {loading ? t('reports.advanced.generating', 'Generating...') : t('reports.advanced.generate', 'Generate Comparison')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Comparison Results */}
      {comparisonResult && comparisonResult.labels && comparisonResult.comparison_data && comparisonResult.comparison_data.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                {t('reports.advanced.results', 'Comparison Results')}
              </CardTitle>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={exportToPDF}
                disabled={exportingPDF}
              >
                <Download className="h-4 w-4 mr-2" />
                {exportingPDF ? t('reports.advanced.exporting', 'Exporting...') : t('reports.advanced.export_pdf', 'Export PDF')}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div ref={comparisonResultsRef} className="space-y-6">
              {/* General Analysis Information */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Number of Entities Compared */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-blue-900">
                    {t('reports.advanced.entities_compared', 'Entities Compared')}
                  </h3>
                  <p className="text-2xl font-bold text-blue-600">
                    {comparisonResult.comparison_data.length}
                  </p>
                  <p className="text-xs text-blue-700 mt-1">
                    {selectedEmployees.length > 0 && `${selectedEmployees.length} ${t('reports.employees', 'employees')}`}
                    {selectedEmployees.length > 0 && selectedCandidates.length > 0 && ', '}
                    {selectedCandidates.length > 0 && `${selectedCandidates.length} ${t('reports.candidates', 'candidates')}`}
                    {(selectedEmployees.length > 0 || selectedCandidates.length > 0) && selectedDepartments.length > 0 && ', '}
                    {selectedDepartments.length > 0 && `${selectedDepartments.length} ${t('reports.departments', 'departments')}`}
                  </p>
                </div>

                {/* Average Compatibility Score */}
                {comparisonResult.compatibility_score !== undefined && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-green-900">
                      {t('reports.advanced.avg_compatibility', 'Avg. Compatibility')}
                    </h3>
                    <p className="text-2xl font-bold text-green-600">
                      {comparisonResult.compatibility_score}%
                    </p>
                    <p className="text-xs text-green-700 mt-1">
                      {t('reports.advanced.vs_reference', 'vs. reference')}
                    </p>
                  </div>
                )}

                {/* Comparison Type */}
                <div className="bg-orange-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-orange-900">
                    {t('reports.advanced.reference_type', 'Reference Type')}
                  </h3>
                  <p className="text-lg font-bold text-orange-600">
                    {compareAgainst === 'employee_average' 
                      ? t('reports.advanced.employee_average', 'Employee Average')
                      : t('reports.advanced.company_profile', 'Company Profile')}
                  </p>
                  <p className="text-xs text-orange-700 mt-1">
                    {t('reports.advanced.baseline', 'Baseline for comparison')}
                  </p>
                </div>

                {/* Data Points */}
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-purple-900">
                    {t('reports.advanced.data_points', 'Data Points')}
                  </h3>
                  <p className="text-2xl font-bold text-purple-600">
                    {comparisonResult.labels ? comparisonResult.labels.length : 10}
                  </p>
                  <p className="text-xs text-purple-700 mt-1">
                    {t('assessment_ui.dimensions_analyzed', 'Dimensions analyzed')}
                  </p>
                </div>
              </div>

              {/* Charts Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Ofertas Chart */}
              <OfertasChart 
                comparisonResult={comparisonResult}
                colors={colors}
                referenceColor={referenceColor}
                t={t}
              />

              {/* Requerimientos Chart */}
              <RequerimientosChart 
                comparisonResult={comparisonResult}
                colors={colors}
                referenceColor={referenceColor}
                t={t}
              />
              </div> {/* End of charts grid */}

              {/* Interpretation Section */}
              {comparisonResult.interpretation && (
                <div className="mt-6 space-y-4">
                  <h3 className="text-lg font-semibold mb-4">{t('reports.interpretation', 'Interpretation')}</h3>
                  
                  {comparisonResult.interpretation.group_summary && (
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                      <h4 className="font-semibold text-blue-900 mb-2">{t('reports.group_summary', 'Group Summary')}</h4>
                      <p className="text-gray-700">{comparisonResult.interpretation.group_summary}</p>
                    </div>
                  )}
                  
                  {comparisonResult.interpretation.strengths_vs_reference && (
                    <div className="bg-green-50 border-l-4 border-green-500 p-4">
                      <h4 className="font-semibold text-green-900 mb-2">{t('reports.strengths', 'Strengths')}</h4>
                      <p className="text-gray-700">{comparisonResult.interpretation.strengths_vs_reference}</p>
                    </div>
                  )}
                  
                  {comparisonResult.interpretation.development_areas_vs_reference && (
                    <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
                      <h4 className="font-semibold text-yellow-900 mb-2">{t('reports.development_areas', 'Development Areas')}</h4>
                      <p className="text-gray-700">{comparisonResult.interpretation.development_areas_vs_reference}</p>
                    </div>
                  )}
                  
                  {comparisonResult.interpretation.consistency_insight && (
                    <div className="bg-purple-50 border-l-4 border-purple-500 p-4">
                      <h4 className="font-semibold text-purple-900 mb-2">{t('reports.consistency', 'Group Consistency')}</h4>
                      <p className="text-gray-700">{comparisonResult.interpretation.consistency_insight}</p>
                    </div>
                  )}
                  
                  {comparisonResult.interpretation.recommendation && (
                    <div className="bg-indigo-50 border-l-4 border-indigo-500 p-4">
                      <h4 className="font-semibold text-indigo-900 mb-2">{t('reports.recommendations', 'Recommendations')}</h4>
                      <p className="text-gray-700">{comparisonResult.interpretation.recommendation}</p>
                    </div>
                  )}
                </div>
              )}
            </div> {/* End of comparisonResultsRef */}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdvancedComparisonSection;
